import { isOrganizerStage, stageEntries } from './conventionKanban';

// Lag cards the board shows at once. Three ~28vw columns plus a fixed-width QR
// track give ~540px per card on a 1920px screen, which is what keeps a full name
// unbroken at 3.2vh — the size it needs to read from across a range. Four columns
// was tried first and truncated names to "Øistein…", which defeats a call-up board.
export const MAX_BOARD_LAGS = 3;

// Live relative time to a start, as Norwegian text. Same wording and thresholds
// components/convention/stageCountdown.js has always produced — moved here so the
// TV board and the in-app badge have one source for the phrasing.
// Returns { label, tone } or null when there is nothing to count down to.
export function formatCountdown(target, nowMs) {
    if (!target) return null;
    const targetMs = target instanceof Date ? target.getTime() : new Date(target).getTime();
    if (Number.isNaN(targetMs)) return null;

    const diff = targetMs - nowMs;
    const abs = Math.abs(diff);
    const days = Math.floor(abs / 86400000);
    const hours = Math.floor((abs % 86400000) / 3600000);
    const mins = Math.floor((abs % 3600000) / 60000);
    let parts;
    if (days > 0) {
        parts = `${days}d ${hours}t`;
    } else if (hours > 0) {
        parts = `${hours}t ${mins}min`;
    } else {
        parts = `${mins}min`;
    }

    if (abs < 60000) return { label: 'nå', tone: 'soon' };
    if (diff > 0) return { label: `om ${parts}`, tone: 'future' };
    return { label: `for ${parts} siden`, tone: 'past' };
}

// The lag a TV board should show: a start time still in the future, not marked
// Ferdig, soonest first. Participants are stored positionally — stages[i] holds
// its shooters in convention.participants{i+1} — so each lag carries that 1-based
// idx and the shooters already placed on it.
//
// Reads only: convention.stages and every participants{N} array are live MobX
// observables shared with the rest of the app, so nothing here sorts or splices
// them in place.
export function upcomingStages(convention, nowMs, { limit = MAX_BOARD_LAGS, userById = {} } = {}) {
    const stages = convention && Array.isArray(convention.stages) ? convention.stages : [];

    const upcoming = [];
    stages.forEach((stage, position) => {
        if (!stage || !stage.date) return;
        if (parseInt(stage.stageDone, 10) > 0) return;
        const startMs = new Date(stage.date).getTime();
        if (Number.isNaN(startMs) || startMs <= nowMs) return;
        upcoming.push({ stage, position, startMs });
    });

    // Array.prototype.sort is stable in every engine we target, but two lag can
    // share a start time — fall back to board order so the result is deterministic.
    upcoming.sort((a, b) => a.startMs - b.startMs || a.position - b.position);

    const totalUpcoming = upcoming.length;
    const capped = limit > 0 ? upcoming.slice(0, limit) : upcoming;

    const lags = capped.map(({ stage, position, startMs }) => {
        const idx = position + 1;
        const list = convention[`participants${idx}`] || [];
        const participants = stageEntries(list).map(({ participant: p, position }) => ({
            id: p.id,
            number: position,
            name: `${p.firstname || ''} ${p.lastname || ''}`.trim(),
            club: p.sportsClub || (userById[p.id] && userById[p.id].sportsClub) || '',
            weapon: p.class || '',
        }));
        return {
            md5: stage.md5,
            name: stage.name || `Lag ${idx}`,
            idx,
            type: stage.type || '',
            date: stage.date,
            startMs,
            isOrganizer: isOrganizerStage(stage),
            slotsUsed: participants.length,
            slotsTotal: stage.stageSlots || 0,
            participants,
        };
    });

    return { lags, hiddenCount: totalUpcoming - lags.length, totalUpcoming };
}
